import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { clientPortalApi } from "./clientPortalApi";
import { useDocumentTitle } from "./useDocumentTitle";
import "./clientPortal.css";

const ERROR_COPY = {
  missing_token: "That link looks incomplete. Request a new one below.",
  invalid_or_expired: "That link has expired or was already used. Request a new one below.",
  no_access: "We couldn't find an active project for that email. Reach out to your Project Manager if this seems wrong.",
  account_conflict: "This email is linked to an existing NJM account and can't be used for client sign-in. Reach out to your Project Manager if this seems wrong.",
};

export default function ClientPortalLogin() {
  useDocumentTitle();
  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get("error");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await clientPortalApi.requestLink(email.trim());
      setSent(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="cp-theme min-h-screen flex items-center justify-center px-6" data-testid="client-portal-login-page">
      <div className="w-full max-w-[420px] cp-enter">
        <div className="flex justify-center mb-10">
          <Logo theme="dark" size="lg" iconOnly />
        </div>
        <div className="cp-card p-8">
          <div className="mb-6">
            <span className="cp-eyebrow">by NJM Productions</span>
            <div className="font-editorial uppercase font-bold tracking-tight text-white text-[26px] mt-1.5">Final Draft</div>
          </div>
          <h1 className="cp-display text-[32px] mt-2 mb-6 text-white">Sign in</h1>

          {errorCode && !sent && (
            <div className="mb-5 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="client-portal-login-error">
              {ERROR_COPY[errorCode] || "Something went wrong. Please try again."}
            </div>
          )}

          {sent ? (
            <div data-testid="client-portal-login-sent" className="cp-enter">
              <p className="text-[15px] leading-relaxed text-[#B7B4AE]">
                If that email is linked to an active NJM project, a sign-in link is on its way. Check your inbox. It's valid for 15 minutes and works once.
              </p>
              <button onClick={() => setSent(false)} data-testid="client-portal-login-resend-btn" className="cp-btn-secondary mt-6 px-4 py-2 text-sm">
                Use a different email
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label htmlFor="cp-email" className="cp-eyebrow block mb-2">Work email</label>
                <input
                  id="cp-email" type="email" required autoFocus value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourcompany.com"
                  data-testid="client-portal-login-email-input"
                  className="cp-input w-full px-4 py-3 text-[15px]"
                />
              </div>
              <button type="submit" disabled={sending} data-testid="client-portal-login-submit-btn" className="cp-btn-primary w-full py-3 text-sm">
                {sending ? "Sending…" : "Send me a sign-in link"}
              </button>
              <p className="text-xs text-[#6E6B66] text-center pt-1">No password needed. We'll email you a secure link.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
